import { onlyDigits } from './masks';
import type { Client } from '@services/requests/housien-models/Client';

type ListClients = (
  agencyId: string,
  page?: number,
  limit?: number,
  filters?: { search?: string },
) => Promise<{ data: Client[] }>;

/**
 * Compares two phone numbers by their digits only.
 */
export function phonesMatch(a: string, b: string): boolean {
  const left = onlyDigits(a);
  const right = onlyDigits(b);
  return Boolean(left) && left === right;
}

/**
 * Extracts the first contact of a given type from a client.
 */
function getContact(client: Client, type: string): string {
  const contacts = client.assignment?.contacts ?? [];
  const found = contacts.find((c) => c.type === type);
  return found?.value ?? '';
}

/**
 * Checks whether a client's name matches a target (case-insensitive).
 */
function namesMatch(client: Client, name: string): boolean {
  const target = name.trim().toLowerCase();
  if (!target) return false;
  const full = (client.personal_info?.full_name ?? '').trim().toLowerCase();
  const corporate = ((client.identity as Record<string, string>)?.corporate_name ?? '').trim().toLowerCase();
  return full === target || corporate === target;
}

/**
 * Resolves an agency client id by matching phone, then name.
 */
export async function findClientIdByPhone(
  agencyId: string,
  phone: string,
  listClients: ListClients,
  name?: string,
): Promise<string | null> {
  const byPhone = await listClients(agencyId, 1, 200);
  const phoneHit = byPhone.data.find((c) => phonesMatch(getContact(c, 'phone'), phone));
  if (phoneHit?.metadata?.client_id) return phoneHit.metadata.client_id;
  if (!name?.trim()) return null;
  const byName = await listClients(agencyId, 1, 50, { search: name.trim() });
  const nameHit = byName.data.find((c) => namesMatch(c, name));
  return nameHit?.metadata?.client_id ?? null;
}
